import React, { useEffect, useMemo, useRef } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from "@/api/instance";
import { AuthContext, type AuthContextValue, type AuthUser } from '@/shared/lib/auth-context';
import { Loader2 } from 'lucide-react';
import { qk } from '@/shared/api/queryKeys';
import { appPath } from '@/shared/lib/appPath';

type MeResponse = {
    upn?: string;
    email?: string | null;
    unique_name?: string | null;
    role?: 'designer' | 'manager' | 'top-manager' | 'developer' | 'unknown';
    salon_id?: string | null;
    available_salons?: { id: string; name: string }[];
    is_guest?: boolean;
};

function toUser(data: MeResponse): AuthUser {
    return {
        username: data.upn!,
        email: data.email,
        unique_name: data.unique_name,
        role: data.role ?? 'unknown',
        salon_id: data.salon_id ?? null,
        available_salons: data.available_salons ?? [],
        is_guest: Boolean(data.is_guest),
    };
}

export default function AuthProvider({ children }: {
    children: React.ReactNode;
}) {
    const meQuery = useQuery({
        queryKey: qk.auth.me(),
        queryFn: async () => {
            const res = await api.instance.get<MeResponse>('/auth/me');
            return toUser(res.data);
        },
        retry: false,
        staleTime: 60_000,
    });

    const isGuestRef = useRef(false);
    useEffect(() => {
        isGuestRef.current = Boolean(meQuery.data?.is_guest);
    }, [meQuery.data?.is_guest]);

    const logoutMutation = useMutation({
        mutationFn: async () => {
            const res = await api.instance.post<{ logout_url?: string | null }>('/auth/logout');
            return res.data?.logout_url ?? null;
        },
        onSettled: (logoutUrl) => {
            // Full-page navigation reloads the app; clearing auth first
            // remounts RequireAuth → empty AppShell before unload.
            const next = isGuestRef.current ? '/recsys' : '/login';
            window.location.replace(logoutUrl ?? appPath(next));
        },
    });

    const user = meQuery.isError ? null : (meQuery.data ?? null);
    const isCheckingAuth = meQuery.isPending;

    const value = useMemo<AuthContextValue>(() => {
        return {
            user,
            isAuthenticated: Boolean(user),
            isCheckingAuth,
            signOut() {
                logoutMutation.mutate();
            },
        };
    }, [user, isCheckingAuth, logoutMutation]);

    if (isCheckingAuth) {
        return (
            <div className="flex h-screen w-full items-center justify-center bg-neutral-background-200">
                <Loader2 className="h-8 w-8 animate-spin text-neutral-text-500" />
            </div>
        );
    }

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
